/**
 * Number and Currency Formatting Utilities
 *
 * Provides consistent formatting for metric values, currencies, and percentages.
 */

/**
 * Format a number with commas and specified decimal places
 * @param {number} value - Number to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted number string
 */
export const formatNumber = (value, decimals = 2) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0';
  }

  return value.toLocaleString('en-GB', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * Format a currency value (GBP)
 * @param {number} value - Currency value
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, decimals = 2) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '£0.00';
  }

  return `£${formatNumber(value, decimals)}`;
};

/**
 * Format a percentage value
 * @param {number} value - Percentage value
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, decimals = 1) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0%';
  }

  return `${formatNumber(value, decimals)}%`;
};

/**
 * Format a metric value based on its unit
 * @param {number} value - Value to format
 * @param {string} unit - Unit type (GBP, %, bottles, etc.)
 * @returns {string} Formatted value string
 */
export const formatMetricValue = (value, unit) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0';
  }

  if (!unit) {
    return formatNumber(value);
  }

  const unitLower = unit.toLowerCase();

  if (unit === 'GBP' || unitLower.includes('gbp')) {
    return formatCurrency(value);
  }

  if (unit === '%' || unitLower.includes('percent') || unitLower === 'pct') {
    // Values are stored as decimals (0.40 = 40%) in METRICS_MART — multiply by 100 for display
    return formatPercentage(value * 100);
  }

  if (unitLower.includes('bottle')) {
    return `${formatNumber(value, 0)} bottles`;
  }

  if (unitLower.includes('customer')) {
    return `${formatNumber(value, 0)} customers`;
  }

  // Default: just format as number
  return formatNumber(value);
};

/**
 * Format a metric value for axis labels (cleaner, no decimal places for currency)
 * @param {number} value - Value to format
 * @param {string} unit - Unit type (GBP, %, bottles, etc.)
 * @returns {string} Formatted value string
 */
export const formatAxisValue = (value, unit) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0';
  }

  if (!unit) {
    return formatNumber(value, 0);
  }

  const unitLower = unit.toLowerCase();

  if (unit === 'GBP' || unitLower.includes('gbp')) {
    // For currency on axes, use no decimals for cleaner display
    return formatCurrency(value, 0);
  }

  if (unit === '%' || unitLower.includes('percent') || unitLower === 'pct') {
    // Values are stored as decimals (0.40 = 40%) in METRICS_MART — multiply by 100 for display
    return formatPercentage(value * 100, 0);
  }

  if (unitLower.includes('bottle')) {
    return formatNumber(value, 0);
  }

  if (unitLower.includes('customer')) {
    return formatNumber(value, 0);
  }

  // Default: no decimals for axis
  return formatNumber(value, 0);
};

/**
 * Format a delta value with + or - sign
 * @param {number} delta - Change value
 * @param {string} unit - Unit type
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted delta string
 */
export const formatDelta = (delta, unit = null, decimals = 2) => {
  if (delta === null || delta === undefined || isNaN(delta)) {
    return '';
  }

  const sign = delta >= 0 ? '+' : '';
  const absValue = Math.abs(delta);

  let formattedValue;
  if (unit === 'GBP') {
    formattedValue = `£${formatNumber(absValue, decimals)}`;
  } else if (unit === '%') {
    formattedValue = `${formatNumber(absValue, decimals)}%`;
  } else {
    formattedValue = formatNumber(absValue, decimals);
  }

  return `${sign}${delta < 0 ? '-' : ''}${formattedValue}`;
};

/**
 * Format a compact number (e.g., 1.2K, 3.5M)
 * @param {number} value - Number to format
 * @returns {string} Compact formatted string
 */
export const formatCompactNumber = (value) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0';
  }

  const absValue = Math.abs(value);

  if (absValue >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }

  if (absValue >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }

  return formatNumber(value, 0);
};

/**
 * Format a date string for display
 * @param {string} dateStr - ISO date string (YYYY-MM-DD)
 * @param {string} format - Format type ('short', 'long', 'month', 'year')
 * @returns {string} Formatted date string
 */
export const formatDate = (dateStr, format = 'short') => {
  if (!dateStr) return '';

  const date = new Date(dateStr);

  if (isNaN(date.getTime())) {
    return dateStr; // Return original if invalid
  }

  switch (format) {
    case 'short':
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });

    case 'long':
      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });

    case 'month':
      return date.toLocaleDateString('en-GB', {
        month: 'short',
        year: 'numeric',
      });

    case 'year':
      return date.toLocaleDateString('en-GB', {
        year: 'numeric',
      });

    default:
      return dateStr;
  }
};

/**
 * Get arrow indicator based on delta and whether positive is good
 * @param {number} delta - Change value
 * @param {boolean} isPositiveGood - Whether positive change is good
 * @returns {object} Object with arrow symbol and color class
 */
export const getDeltaIndicator = (delta, isPositiveGood = true) => {
  if (delta === null || delta === undefined || isNaN(delta) || delta === 0) {
    return { arrow: '', colorClass: 'neutral' };
  }

  const isPositive = delta > 0;
  const isGood = isPositive === isPositiveGood;

  return {
    arrow: isPositive ? '↑' : '↓',
    colorClass: isGood ? 'positive' : 'negative',
  };
};

export default {
  formatNumber,
  formatCurrency,
  formatPercentage,
  formatMetricValue,
  formatAxisValue,
  formatDelta,
  formatCompactNumber,
  formatDate,
  getDeltaIndicator,
};
